import { createContext, useContext, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  setAssumedRoles,
  PROJECT_ASSUME_SCOPE,
} from '../../../api/assumedRoles';
import { bookingKeys } from '../../../api/bookingQueries';
import type { components } from '../../../api/generated/hsBookingApi';
import type { Stored } from '../../../api/stored';

type Project = Stored<components['schemas']['HsBookingProject']>;

type AssumedRole = {
  assumedProjects: Project[];
  assumeProject: (project: Project) => void;
  leaveProject: (project: Project) => void;
};

/*
 * Projects are "unassumed": their list is visible, but everything below
 * a project (booking items, hosting assets) only becomes accessible
 * after assuming the project admin role. Several projects can be
 * entered at the same time — their roles are sent semicolon-separated
 * and only apply to booking/hosting requests (see assumedRoles.ts).
 * The assumption is deliberately not persisted — a reload starts
 * unassumed again.
 */
const AssumedRoleContext = createContext<AssumedRole | null>(null);

/* the role entering a project assumes — also the checkbox tooltip */
export const projectAdminRole = (project: Project) =>
  `hs_booking.project#${project.uuid}:ADMIN`;

export function AssumedRoleProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const queryClient = useQueryClient();
  const [assumedProjects, setAssumedProjects] = useState<Project[]>([]);

  const applyAssumption = (projects: Project[]) => {
    setAssumedRoles(
      PROJECT_ASSUME_SCOPE,
      projects.length ? projects.map(projectAdminRole).join(';') : null
    );
    setAssumedProjects(projects);
  };

  const assumeProject = (project: Project) => {
    applyAssumption([
      ...assumedProjects.filter((entered) => entered.uuid !== project.uuid),
      project,
    ]);
  };

  const leaveProject = (project: Project) => {
    applyAssumption(
      assumedProjects.filter((entered) => entered.uuid !== project.uuid)
    );
    queryClient.removeQueries({ queryKey: bookingKeys.items(project.uuid) });
  };

  return (
    <AssumedRoleContext.Provider
      value={{ assumedProjects, assumeProject, leaveProject }}
    >
      {children}
    </AssumedRoleContext.Provider>
  );
}

export function useAssumedRole(): AssumedRole {
  const context = useContext(AssumedRoleContext);
  if (!context) {
    throw new Error('useAssumedRole requires an AssumedRoleProvider');
  }
  return context;
}
